import { useCallback, useEffect, useState } from "preact/hooks";
import type { ItemDetail, Member, SpaceOut } from "../api/types.gen.ts";
import { t } from "../i18n/index.ts";
import { useItem } from "../lib/data.ts";
import { entityOf } from "../lib/entities.ts";
import { isFolderId } from "../lib/folders.ts";
import { formatInstant, formatRelative } from "../lib/format.ts";
import { filesOf, isOwnEmail } from "../lib/itemFiles.ts";
import { spaceUrl } from "../lib/paths.ts";
import { peek, peeked, useHandOver } from "../lib/peek.ts";
import { usePeople } from "../lib/people.ts";
import { bump, type Resource } from "../lib/resource.ts";
import { canEdit } from "../lib/roles.ts";
import { isPlainClick } from "../lib/router.ts";
import { issueKey } from "../lib/state.ts";
import { titleOf } from "../lib/titles.ts";
import { BookmarkButton } from "../ui/BookmarkButton.tsx";
import { Link } from "../ui/Button.tsx";
import { IssueBadges, ItemCommentCount, LevelIcon } from "../ui/counts.tsx";
import { Async } from "../ui/misc.tsx";
import { PeekFrame } from "../ui/PeekFrame.tsx";
import { PresenceStack } from "../ui/PresenceStack.tsx";
import { buttonClass, LINK } from "../ui/styles.ts";
import { RichText } from "./comments/richText.tsx";
import { ItemPlace } from "./folders/itemPlace.tsx";
import { FolderLook } from "./folders/look.tsx";
import { DocumentEditing } from "./item/document.tsx";
import { ItemFields } from "./item/form.tsx";
import { ItemHeading } from "./item/heading.tsx";
import { EmailBody, Preview } from "./item/panels.tsx";
import { EntityLabel } from "./items/entityLabel.tsx";
import { KindIcon } from "./items/icons.tsx";

/** What the item's page has besides: its issues, comments, what refers to it, when it changed (each to its tab). */
function Facts({ space, item }: { space: SpaceOut; item: ItemDetail }) {
  const page = spaceUrl(space, `/items/${item.id}`);
  const counts = { error: 0, warning: 0, info: 0 };
  for (const i of item.issues) {
    counts[i.severity] += 1;
  }
  const refs = item.referenced_by.length;
  return (
    <p class="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-zinc-500">
      {/* What it came as, when renamed since, as its page says it. */}
      {item.original_title ? (
        <span class="min-w-0 truncate" title={t("item.originalName", { name: item.original_title })}>
          {item.original_title}
        </span>
      ) : null}
      <EntityLabel entity={entityOf(space, item.entity)} />
      {/* To its page, where its issues are (beside its fields, or its file). */}
      <IssueBadges counts={counts} href={() => page} />
      {refs > 0 ? (
        <Link href={`${page}?tab=referenced`} class="hover:underline">
          {t("item.referencedBy")} <span class="tabular-nums">{refs}</span>
        </Link>
      ) : null}
      <span title={formatInstant(item.updated_at)}>
        {t("items.cardUpdated", { when: formatRelative(item.updated_at) })}
      </span>
      {/* Last: with none, it's there only on hover (no gap mid-line). */}
      <ItemCommentCount space={space} id={item.id} />
    </p>
  );
}

/**
 * The item's files (its own, then attached), one shown as tall as the look; several: a choice of which. A document or
 * spreadsheet is edited right here (Edit): the same editor as its page's, its Save version and Done in its bar.
 */
function FilesLook({ space, item }: { space: SpaceOut; item: ItemDetail }) {
  const files = filesOf(item);
  const [at, setAt] = useState(0);
  // The file being edited here (by its item's id).
  const [editing, setEditing] = useState<string | null>(null);
  const shown = files[Math.min(at, files.length - 1)];
  if (!shown) {
    return null;
  }
  return (
    // As tall as the look (`data-fill`: a spreadsheet's grid too, not its own height cap).
    <div class="flex min-h-0 flex-1 flex-col gap-3 p-4" data-fill="">
      {files.length > 1 ? (
        <div role="tablist" aria-label={t("item.tab.files", { count: files.length })} class="flex flex-wrap gap-1">
          {files.map((f, i) => (
            <button
              key={f.id}
              type="button"
              role="tab"
              aria-selected={f === shown}
              // Not while one's being edited: Done first (it saves).
              disabled={editing !== null && f !== shown}
              class="flex max-w-60 items-center gap-1.5 rounded px-2 py-1 text-xs text-zinc-600 hover:bg-zinc-100 aria-selected:bg-zinc-100 aria-selected:text-zinc-900"
              onClick={() => setAt(i)}
            >
              <KindIcon item={{ kind: "file", file: f.file, title: f.title }} />
              <span class="truncate">{titleOf(f)}</span>
            </button>
          ))}
        </div>
      ) : null}
      {editing === shown.id ? (
        <DocumentEditing
          key={shown.id}
          space={space}
          shown={shown}
          fill={true}
          actions={true}
          onDone={() => {
            setEditing(null);
            bump();
          }}
        />
      ) : isOwnEmail(shown) ? (
        <div class="min-h-0 flex-1 overflow-auto">
          <EmailBody space={space} item={item} shown={shown} />
        </div>
      ) : (
        <Preview
          key={shown.id}
          space={space}
          shown={shown}
          fill={true}
          onEdit={canEdit(space) ? () => setEditing(shown.id) : undefined}
          view="peek"
        />
      )}
    </div>
  );
}

/** An item without a file of its own: its filled fields, open issues, and attached files (each looked at in turn). */
function DetailsLook({
  space,
  r,
  item,
  members,
}: {
  space: SpaceOut;
  r: Resource<ItemDetail>;
  item: ItemDetail;
  members: readonly Member[] | undefined;
}) {
  const files = filesOf(item);
  return (
    <div class="flex flex-col gap-6 p-4 text-sm">
      {/* Its fields as its page edits them (all of them, empty ones too). */}
      <ItemFields space={space} r={r} item={item} members={members} />
      {item.issues.length > 0 ? (
        <ul class="flex flex-col gap-2" aria-label={t("item.findings", { count: item.issues.length })}>
          {item.issues.map((i) => (
            <li key={issueKey(i)} class="flex items-start gap-2">
              <span class="mt-0.5 shrink-0" title={t(`severity.${i.severity}`)}>
                <LevelIcon level={i.severity} />
              </span>
              <RichText space={space} text={i.message} inline={true} class="text-zinc-800" />
            </li>
          ))}
        </ul>
      ) : null}
      {files.length > 0 ? (
        <ul class="flex flex-col gap-1" aria-label={t("item.tab.files", { count: files.length })}>
          {files.map((f) => (
            <li key={f.id} class="flex items-center gap-2">
              <KindIcon item={{ kind: "file", file: f.file, title: f.title }} />
              <Link
                href={spaceUrl(space, `/items/${f.id}`)}
                class={LINK}
                onClick={(e) => {
                  if (isPlainClick(e)) {
                    e.preventDefault();
                    peek(f.id, e.currentTarget);
                  }
                }}
              >
                {titleOf(f)}
              </Link>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function ItemLook({
  space,
  id,
  leaving,
  onGone,
}: {
  space: SpaceOut;
  id: string;
  leaving: boolean;
  onGone: () => void;
}) {
  const r = useItem(space, id);
  const item = r.data;
  const members = usePeople();
  // It slides in once there's something to show (not half-drawn); then it stays while the next item loads.
  const [ready, setReady] = useState(false);
  const show = ready || item !== undefined || r.error !== undefined;
  if (show && !ready) {
    setReady(true);
  }
  useEffect(() => {
    if (!show && leaving) {
      onGone();
    }
  }, [show, leaving, onGone]);
  if (!show) {
    return null;
  }
  return (
    <PeekFrame
      leaving={leaving}
      onGone={onGone}
      label={item ? titleOf(item) : t("common.loading")}
      title={
        item ? (
          <>
            {/* As the item's page heads it. */}
            <ItemHeading item={item} members={members} look={true} />
            <ItemPlace space={space} item={item} />
            <Facts space={space} item={item} />
          </>
        ) : null
      }
      actions={
        <>
          {/* Who else is on it (you're there too, in the quick look). */}
          <PresenceStack room={`item:${id}`} view="peek" size={24} />
          <Link href={spaceUrl(space, `/items/${id}`)} class={buttonClass("secondary", "sm")}>
            {t("peek.open")}
          </Link>
          <BookmarkButton space={space} target={{ kind: "item", item: id }} />
        </>
      }
    >
      <Async r={r} whole="panel">
        {(d) =>
          d.file ? (
            <FilesLook key={d.id} space={space} item={d} />
          ) : (
            <DetailsLook key={d.id} space={space} r={r} item={d} members={members} />
          )
        }
      </Async>
    </PeekFrame>
  );
}

/** What's looked at (`?peek=`: an item, or a folder), if anything, over the page; once closed, still there while it slides out. */
export function ItemPeek({ space }: { space: SpaceOut }) {
  const id = peeked();
  // In the app's own sheet instead, where looks are its.
  const handed = useHandOver(id);
  const [last, setLast] = useState<string | null>(null);
  if (id && id !== last && !handed) {
    setLast(id);
  }
  const onGone = useCallback(() => setLast(null), []);
  const shown = handed ? last : (id ?? last);
  if (!shown) {
    return null;
  }
  // A folder looked at (its own look), or an item.
  return isFolderId(shown) ? (
    <FolderLook space={space} id={shown} leaving={id === null} onGone={onGone} />
  ) : (
    <ItemLook space={space} id={shown} leaving={id === null} onGone={onGone} />
  );
}
